import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Icon from '../../components/AppIcon';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import ActiveBookingsTab from './components/ActiveBookingsTab';
import BundleManagementTab from './components/BundleManagementTab';
import InvoiceCenterTab from './components/InvoiceCenterTab';
import LiveTrackingTab from './components/LiveTrackingTab';
import PendingOrdersTab from './components/PendingOrdersTab';
import MySellOffersTab from './components/MySellOffersTab';
import DoFinanceModal from '../../components/DoFinanceModal';
import { PERMISSION_KEYS, hasPermission } from '../../data/userPermissions';

const TAB_STORAGE_KEY = 'sugarHathi_buyerDashboard_activeTab';
const VALID_TABS = ['pending', 'bookings', 'bundles', 'invoices', 'tracking', 'sell-offers'];

const BuyerOperationsDashboard = () => {
  const getSavedTab = () => {
    const saved = localStorage.getItem(TAB_STORAGE_KEY);
    return saved && VALID_TABS?.includes(saved) ? saved : 'bookings';
  };

  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() => {
    // Check if navigation state has a specific tab to open
    const navTab = location?.state?.activeTab;
    if (navTab && VALID_TABS?.includes(navTab)) return navTab;
    return getSavedTab();
  });
  const [trackingBookingId, setTrackingBookingId] = useState(null);
  const [doFinanceApp, setDoFinanceApp] = useState(null);
  const [doFinanceModalOpen, setDoFinanceModalOpen] = useState(false);
  const [doFinanceAccess, setDoFinanceAccess] = useState(false);
  // Owner/employee/partner account linking — null means this account is
  // its own owner (the normal, unlinked case). relation_type is only
  // meaningful when accountLink is non-null.
  const [accountLink, setAccountLink] = useState(null);
  const [employeeBlockedOpen, setEmployeeBlockedOpen] = useState(false);
  const [reportStats, setReportStats] = useState({
    totalOrders: 0,
    totalSpend: 0,
    avgPrice: 0,
    topFactory: '—',
    loading: true,
  });
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    localStorage.setItem(TAB_STORAGE_KEY, tabId);
  };

  const loadReportStats = useCallback(async () => {
    if (!user?.id) {
      setReportStats({ totalOrders: 0, totalSpend: 0, avgPrice: 0, topFactory: '—', loading: false });
      return;
    }

    // Query buyer_bookings by buyer_id (UUID)
    const { data: bookings, error } = await supabase?.from('buyer_bookings')?.select('*', { count: 'exact' })?.eq('buyer_id', user?.id);

    console.log('[BuyerDashboard] buyer_bookings query:', { userId: user?.id, bookings, error });

    const orders = bookings || [];
    let totalSpend = 0;
    let allPrices = [];
    const factoryMap = {};

    orders?.forEach(o => {
      const qty = parseFloat(o?.quantity) || 0;
      const rate = parseFloat(o?.price_per_ton || o?.rate) || 0;
      totalSpend += qty * rate;
      if (rate > 0) allPrices?.push(rate);
      const name = o?.factory_name || o?.mill_name || 'Unknown';
      factoryMap[name] = (factoryMap?.[name] || 0) + 1;
    });

    const avgPrice = allPrices?.length > 0
      ? allPrices?.reduce((s, p) => s + p, 0) / allPrices?.length
      : 0;

    const topFactory = Object.entries(factoryMap)?.sort((a, b) => b?.[1] - a?.[1])?.[0]?.[0] || '—';

    setReportStats({
      totalOrders: orders?.length,
      totalSpend,
      avgPrice,
      topFactory,
      loading: false,
    });
  }, [user]);

  useEffect(() => {
    loadReportStats();

    // Real-time subscription on buyer_bookings for stats refresh
    const channel = supabase?.channel('buyer_dashboard_bookings')?.on('postgres_changes', { event: '*', schema: 'public', table: 'buyer_bookings' }, () => {
        loadReportStats();
      })?.subscribe();

    return () => supabase?.removeChannel(channel);
  }, [loadReportStats]);

  // Fetch account link once — determines whether this account is its own
  // owner, a linked partner (read-only DO Finance visibility), or a
  // linked employee (DO Finance fully blocked regardless of consent).
  useEffect(() => {
    const loadAccountLink = async () => {
      if (!user?.id) { setAccountLink(null); return; }
      // Best-effort claim of any pending invite addressed to this
      // account's mobile/email — non-fatal if there's nothing to claim,
      // never blocks the rest of the dashboard from loading.
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          await supabase.functions.invoke('claim-account-link', {
            headers: { Authorization: `Bearer ${session.access_token}` },
          });
        }
      } catch (e) {
        console.error('claim-account-link (non-fatal):', e);
      }
      const { data } = await supabase
        ?.from('business_account_links')
        ?.select('*')
        ?.eq('linked_user_id', user?.id)
        ?.eq('status', 'active')
        ?.maybeSingle();
      setAccountLink(data ?? null);
    };
    loadAccountLink();
  }, [user]);

  useEffect(() => {
    const loadDoFinanceStatus = async () => {
      if (!user?.id) { setDoFinanceApp(null); return; }
      // Partner accounts have no application of their own — show the
      // owner's status, read-only. Employees never reach a state where
      // this data is displayed (access is blocked outright), so owner's
      // own id is fine as a harmless default for them too.
      const statusOwnerId = accountLink?.relation_type === 'partner'
        ? accountLink.owner_user_id
        : user?.id;
      const { data } = await supabase
        ?.from('trader_od_applications')
        ?.select('id, status, approved_vehicle_limit, limit_status')
        ?.eq('user_id', statusOwnerId)
        ?.order('created_at', { ascending: false })
        ?.limit(1);
      setDoFinanceApp(data?.[0] ?? null);
    };
    loadDoFinanceStatus();
  }, [user, accountLink]);

  // DO Finance secondary permission: gates the "Need DO Finance?" /
  // "Use My DO Finance" button pair together. Checked once per login/user
  // change, same pattern as Factory Directory in RoleBasedHeader.jsx.
  // For a linked partner, check the OWNER's permission grant — a partner
  // was never separately granted this permission themselves, so checking
  // their own id would incorrectly hide the read-only view entirely.
  useEffect(() => {
    if (!user?.id) {
      setDoFinanceAccess(false);
      return;
    }
    const checkUserId = accountLink ? accountLink.owner_user_id : user?.id;
    let cancelled = false;
    hasPermission(checkUserId, PERMISSION_KEYS?.DO_FINANCE)?.then((enabled) => {
      if (!cancelled) setDoFinanceAccess(!!enabled);
    });
    return () => {
      cancelled = true;
    };
  }, [user, accountLink]);

  const tabs = [
    { id: 'pending', label: t('buyerDashboard.tabs.pending'), icon: 'Clock' },
    { id: 'bookings', label: t('buyerDashboard.tabs.bookings'), icon: 'ShoppingCart' },
    { id: 'bundles', label: t('buyerDashboard.tabs.bundles'), icon: 'Package' },
    { id: 'invoices', label: t('buyerDashboard.tabs.invoices'), icon: 'FileText' },
    { id: 'tracking', label: t('buyerDashboard.tabs.tracking'), icon: 'Navigation' },
    { id: 'sell-offers', label: t('buyerDashboard.tabs.sellOffers'), icon: 'Tag' },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'pending':
        return <PendingOrdersTab />;
      case 'bookings':
        return <ActiveBookingsTab onSwitchToTracking={(bookingId) => { setTrackingBookingId(bookingId); handleTabChange('tracking'); }} />;
      case 'bundles':
        return <BundleManagementTab onSwitchToTracking={(bookingId) => { setTrackingBookingId(bookingId); handleTabChange('tracking'); }} />;        
      case 'invoices':
        return <InvoiceCenterTab />;
      case 'tracking':
        return <LiveTrackingTab bookingId={trackingBookingId} />;
      case 'sell-offers':
        return <MySellOffersTab />;
      default:
        return <ActiveBookingsTab onSwitchToTracking={(bookingId) => { setTrackingBookingId(bookingId); handleTabChange('tracking'); }} />;
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#f8f1e9' }}>
      <RoleBasedHeader />
      <BreadcrumbNavigation />
      <main className="main-content">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="mb-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #f97316, #ea580c)' }}>
                <Icon name="LayoutDashboard" size={24} color="#FFFFFF" />
              </div>
              <div>
                <h1 className="text-3xl font-bold" style={{ color: '#1a1a1a' }}>{t('buyerDashboard.title')}</h1>
                <p className="text-sm mt-0.5" style={{ color: '#6b7280' }}>{t('buyerDashboard.subtitle')}</p>
              </div>
            </div>
            {/* Quick Action Buttons */}
            <div className="grid grid-cols-2 gap-3 mt-5">
              <button
                onClick={() => navigate('/sugar-marketplace-dashboard')}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white transition-all duration-200 shadow-md hover:shadow-lg"
                style={{ background: 'linear-gradient(135deg, #f97316, #ea580c)' }}
              >
                <Icon name="ShoppingBag" size={16} />
                {t('buyerDashboard.browseSugar')}
              </button>
              <button
                onClick={() => navigate('/sell-your-sugar')}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white transition-all duration-200 shadow-md hover:shadow-lg"
                style={{ background: 'linear-gradient(135deg, #f97316, #ea580c)' }}
              >
                <Icon name="Tag" size={16} />
                {t('buyerDashboard.sellYourSugar')}
              </button>
              {doFinanceAccess && accountLink?.relation_type === 'partner' && (
                <div
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold"
                  style={{ backgroundColor: '#eff6ff', color: '#1e4a72', border: '1.5px solid #bfdbfe' }}
                >
                  <Icon name="Eye" size={16} />
                  DO Finance: {doFinanceApp?.status === 'approved' ? `Approved (${doFinanceApp?.limit_status})` : doFinanceApp?.status ? doFinanceApp.status : 'Not yet applied'} — view only
                </div>
              )}
              {doFinanceAccess && accountLink?.relation_type === 'employee' && (
                <>
                  <button
                    onClick={() => setEmployeeBlockedOpen(true)}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white transition-all duration-200 shadow-md hover:shadow-lg"
                    style={{ background: 'linear-gradient(135deg, #f97316, #ea580c)' }}
                  >
                    <Icon name="Gavel" size={16} />
                    Need DO Finance?
                  </button>
                  <button
                    onClick={() => setEmployeeBlockedOpen(true)}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white transition-all duration-200 shadow-md hover:shadow-lg"
                    style={{ background: 'linear-gradient(135deg, #f97316, #ea580c)' }}
                  >
                    <Icon name="IndianRupee" size={16} />
                    Use My DO Finance
                  </button>
                </>
              )}
              {doFinanceAccess && !accountLink && (
                <>
                  <button
                    onClick={() => navigate('/do-finance/apply')}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white transition-all duration-200 shadow-md hover:shadow-lg"
                    style={{ background: 'linear-gradient(135deg, #f97316, #ea580c)' }}
                  >
                    <Icon name="Gavel" size={16} />
                    {doFinanceApp?.status === 'approved' ? 'Enhance My Limit' : 'Need DO Finance?'}
                  </button>
                  <button
                    onClick={() => setDoFinanceModalOpen(true)}
                    disabled={!(doFinanceApp?.status === 'approved' && doFinanceApp?.limit_status === 'active')}
                    title={doFinanceApp?.status === 'approved' && doFinanceApp?.limit_status === 'active' ? '' : 'Available once your DO Finance limit is approved and active'}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all duration-200"
                    style={
                      doFinanceApp?.status === 'approved' && doFinanceApp?.limit_status === 'active'
                        ? { background: 'linear-gradient(135deg, #f97316, #ea580c)', color: '#fff', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }
                        : { backgroundColor: '#e5e7eb', color: '#9ca3af', cursor: 'not-allowed' }
                    }
                  >
                    <Icon name="IndianRupee" size={16} />
                    Use My DO Finance
                  </button>
                </>
              )}
            </div>
          </div>

          {/* ── My Reports Section ── */}
          <div className="mb-8 bg-white rounded-2xl border shadow-sm overflow-hidden" style={{ borderColor: '#e5e7eb' }}>
            <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: '#f3f4f6', backgroundColor: '#fffbf5' }}>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#d1fae5' }}>
                  <Icon name="BarChart2" size={18} color="#065f46" />
                </div>
                <div>
                  <h2 className="text-base font-bold" style={{ color: '#1f2937' }}>{t('buyerDashboard.myReports')}</h2>
                  <p className="text-xs" style={{ color: '#6b7280' }}>{t('buyerDashboard.purchaseAnalytics')}</p>
                </div>
              </div>
              <button
                onClick={() => navigate('/reports-generation-center')}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 shadow-sm"
                style={{ backgroundColor: '#065f46' }}
              >
                <Icon name="ExternalLink" size={14} />
                {t('buyerDashboard.viewFullReports')}
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-0 divide-x divide-y sm:divide-y-0" style={{ divideColor: '#f3f4f6' }}>
              <div className="p-4 flex flex-col gap-1">
                <div className="flex items-center gap-1.5 mb-1">
                  <Icon name="ShoppingCart" size={14} color="#f97316" />
                  <span className="text-xs font-medium" style={{ color: '#6b7280' }}>{t('buyerDashboard.totalOrders')}</span>
                </div>
                {reportStats?.loading ? (
                  <div className="h-8 w-12 rounded animate-pulse" style={{ background: '#f3f4f6' }} />
                ) : (
                  <p className="text-2xl font-bold" style={{ color: '#1f2937' }}>
                    {reportStats?.totalOrders > 0 ? reportStats?.totalOrders : '0'}
                  </p>
                )}
                <p className="text-xs" style={{ color: '#9ca3af' }}>{t('buyerDashboard.purchaseHistory')}</p>
              </div>
              <div className="p-4 flex flex-col gap-1">
                <div className="flex items-center gap-1.5 mb-1">
                  <Icon name="IndianRupee" size={14} color="#f97316" />
                  <span className="text-xs font-medium" style={{ color: '#6b7280' }}>{t('buyerDashboard.totalSpend')}</span>
                </div>
                {reportStats?.loading ? (
                  <div className="h-8 w-16 rounded animate-pulse" style={{ background: '#f3f4f6' }} />
                ) : (
                  <p className="text-2xl font-bold" style={{ color: '#1f2937' }}>
                    {reportStats?.totalSpend > 0
                      ? `₹${(reportStats?.totalSpend / 100000)?.toFixed(1)}L`
                      : '—'}
                  </p>
                )}
                <p className="text-xs" style={{ color: '#9ca3af' }}>{t('buyerDashboard.costTrends')}</p>
              </div>
              <div className="p-4 flex flex-col gap-1">
                <div className="flex items-center gap-1.5 mb-1">
                  <Icon name="TrendingUp" size={14} color="#f97316" />
                  <span className="text-xs font-medium" style={{ color: '#6b7280' }}>{t('buyerDashboard.avgPriceTon')}</span>
                </div>
                {reportStats?.loading ? (
                  <div className="h-8 w-16 rounded animate-pulse" style={{ background: '#f3f4f6' }} />
                ) : (
                  <p className="text-2xl font-bold" style={{ color: '#1f2937' }}>
                    {reportStats?.avgPrice > 0
                      ? `₹${reportStats?.avgPrice?.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
                      : '—'}
                  </p>
                )}
                <p className="text-xs" style={{ color: '#9ca3af' }}>{t('buyerDashboard.spendingAnalytics')}</p>
              </div>
              <div className="p-4 flex flex-col gap-1">
                <div className="flex items-center gap-1.5 mb-1">
                  <Icon name="Building2" size={14} color="#f97316" />
                  <span className="text-xs font-medium" style={{ color: '#6b7280' }}>{t('buyerDashboard.topFactory')}</span>
                </div>
                {reportStats?.loading ? (
                  <div className="h-8 w-24 rounded animate-pulse" style={{ background: '#f3f4f6' }} />
                ) : (
                  <p className="text-base font-bold truncate" style={{ color: '#1f2937' }}>{reportStats?.topFactory}</p>
                )}
                <p className="text-xs" style={{ color: '#9ca3af' }}>{t('buyerDashboard.spendingByFactory')}</p>
              </div>
            </div>
          </div>

          {/* Desktop Tabs */}
          <div className="hidden md:block rounded-xl border mb-6 p-2"
            style={{ background: '#ffffff', borderColor: '#e5e7eb' }}>
            <div className="flex items-center gap-2 overflow-x-auto">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => handleTabChange(tab?.id)}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap"
                  style={
                    activeTab === tab?.id
                      ? { background: 'linear-gradient(135deg, #f97316, #ea580c)', color: '#ffffff' }
                      : { color: '#374151', background: 'transparent', fontWeight: '700' }
                  }
                >
                  <Icon name={tab?.icon} size={17} />
                  <span>{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Mobile Tabs */}
          <div className="md:hidden mb-6">
            <div className="relative">
              <select
                value={activeTab}
                onChange={(e) => handleTabChange(e?.target?.value)}
                className="w-full px-4 py-3 rounded-lg font-medium appearance-none cursor-pointer focus:outline-none"
                style={{
                  background: '#ffffff',
                  color: '#1a1a1a',
                  border: '1px solid #e5e7eb',
                  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23f97316' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                  backgroundPosition: 'right 0.75rem center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '1.5em 1.5em',
                  paddingRight: '3rem'
                }}
              >
                {tabs?.map((tab) => (
                  <option key={tab?.id} value={tab?.id}>
                    {tab?.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="transition-all duration-300">
            {renderTabContent()}
          </div>
        </div>
      </main>
      <DoFinanceModal isOpen={doFinanceModalOpen} onClose={() => setDoFinanceModalOpen(false)} />

      {employeeBlockedOpen && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(30,41,59,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 16,
        }}>
          <div style={{
            width: '100%', maxWidth: 420, background: '#fff', borderRadius: 20, overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          }}>
            <div style={{ background: 'linear-gradient(135deg, #1e4a72, #15375a)', padding: '22px 26px 18px' }}>
              <div style={{
                width: 44, height: 44, borderRadius: '50%', background: 'rgba(217,119,6,0.18)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, marginBottom: 10,
              }}>🔒</div>
              <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#fff' }}>
                DO Finance — Owner Only
              </h2>
              <p style={{ margin: '4px 0 0', fontSize: 12.5, color: '#a8c4d8', lineHeight: 1.5 }}>
                Your parent/promoter must apply for DO Finance. This isn't available on employee accounts.
              </p>
            </div>
            <div style={{ padding: '20px 26px 26px' }}>
              <button
                type="button"
                onClick={() => setEmployeeBlockedOpen(false)}
                style={{
                  width: '100%', padding: '14px',
                  background: 'linear-gradient(135deg, #d97706, #b45309)',
                  color: '#fff', border: 'none', borderRadius: 10,
                  fontSize: 14.5, fontWeight: 700, cursor: 'pointer',
                }}>
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BuyerOperationsDashboard;
